
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { format } from '@piconetworks/pkg-currency'
import { H2, H3, H4 } from '@piconetworks/pkg-heading'
import Boutton from '@piconetworks/pkg-boutton'

import usePublisherId from 'hooks/usePublisherId'
import useUserId from 'hooks/useEmail'
import useUserCount from 'hooks/useUserCount'
import useUserChunks from 'hooks/useUserChunks'

import { useFeatureManager } from 'features/FeatureManager'
import { PlanCreditSummary } from 'features/planCreditSummary'

import { calculateHypePluginPricing } from 'utils/lib/pricing'
import AccountModule from 'modules/account'
import Cancellation from '../Generic.Cancellation'

import styles from './PlanDetail.module.scss'

const SubscriptionDetail = ({
    appLabel = null,
    showUpgrade = () => {},
}) => {
    const userId = useUserId()
    const publisherId = usePublisherId()
    const userCount = useUserCount()
    const userChunks = useUserChunks()
    const { getAppByLabel, getAppTierByLabel } = useFeatureManager({
        userId,
        publisherId,
    })
    const app = getAppByLabel(appLabel)
    const tiers = app?.tiers
    const tierLabelGrantingAccess = tiers.find(({ has_access }) => !!has_access)?.label

    let appTier = getAppTierByLabel(tierLabelGrantingAccess)

    const hasPhoneNumber = useSelector(AccountModule.selectors.hasPhoneNumber)
    const hasFreeMessagingAccess = hasPhoneNumber && !tierLabelGrantingAccess
    const isHypePlugin = appLabel === 'hype-plugin'
    const isMessaging = appLabel === 'messages'
    
    if (isMessaging && hasFreeMessagingAccess) {
        appTier = tiers.find(tier => tier.id === 'free-99')
    }

    const formattedUserCount = Intl.NumberFormat('en-US').format(Number(userCount))

    const appTierName = isHypePlugin ? `${formattedUserCount} Contacts` : appTier?.name
    const creditTypes = appTier?.creditTypes

    const currentPricing = appTier?.pricings?.find((pricing) => {
        return !!pricing?.subscription && pricing?.subscription?.status !== 'canceled'
    })

    const currentCanceledPricing = appTier?.pricings?.find((pricing) => {
        return !!pricing?.subscription && pricing?.subscription?.status === 'canceled'
    })

    const pricingId = currentPricing?.id

    const amount = currentPricing?.amount
    const currency = currentPricing?.currency

    let cost = format({
        amount,
        currency,
        symbol: true,
    })

    const currentPeriodEnd = currentPricing?.subscription?.current_period_end
    const nextBillingAmount = currentPricing?.subscription?.plan?.amount
    const nextBillingCurrency = currentPricing?.subscription?.plan?.currency
    const currentPeriodEndIfCanceled = currentCanceledPricing?.subscription?.current_period_end
    const currentPeriodEndString = dayjs(currentPeriodEnd * 1000, 'X').format('MMM D, YYYY')
    const currentCanceledPeriodEndString = dayjs(currentPeriodEndIfCanceled * 1000, 'X').format('MMM D, YYYY')

    let nextBillingAmountFormatted = format({
        amount: nextBillingAmount,
        currency: nextBillingCurrency,
        symbol: true
    })

    if (isHypePlugin) {
        const hypePluginPrice = calculateHypePluginPricing(userCount)

        const price = format({
            amount: hypePluginPrice * 100,
            currencyCode: currency,
            symbol: true,
        })

        cost = hypePluginPrice === 0 ? 'FREE' : `${price}`
        nextBillingAmountFormatted = price
    }

    if (!appTier || (!appTier && isMessaging && !hasFreeMessagingAccess)) {
        return null
    }

    return (
        <section className={styles.planContainer}>
            <div className={styles.header}>
                <H2>Subscription</H2>
                <div className={styles.cta}>
                    {tiers.length > 1 && (
                        <Boutton
                            onClick={showUpgrade}
                            variant="secondary"
                        >
                            Change plan
                        </Boutton>
                    )}
                    {!hasFreeMessagingAccess && currentPricing && (
                        <Cancellation
                            app={appTier}
                            pricingId={pricingId}
                        />
                    )}
                </div>  
            </div>
            <div className={styles.planInfo}>
                <H3>{appTierName}</H3>
                <H4>{cost}</H4>
            </div>
            {isHypePlugin && (
                <div className={styles.costSummary}>
                    {userChunks > 1 ?
                        '$5 per month per 500 contacts' :
                        'Free for up to 500 contacts'
                    }
                </div>
            )
            }
            {creditTypes.map((creditType) => (
                <PlanCreditSummary
                    key={`_${creditType}`}
                    creditType={creditType}
                />
            ))}
            {(currentPeriodEnd || currentPeriodEndIfCanceled) && (cost !== "FREE") && (
                <div className={styles.planRenewal}>
                    <div className={styles.planNext}>
                        { currentPeriodEnd ? 
                            `Next payment on ${currentPeriodEndString}` :
                            `Subscription will end on ${currentCanceledPeriodEndString}`
                        }
                    </div>
                    {currentPeriodEnd && (
                        <div className={styles.planPrice}>{nextBillingAmountFormatted}</div>
                    )}
                </div>
            )}
        </section>
    )
}

export default SubscriptionDetail
